// Pure date utilities. No D1, no Cloudflare types.
//
// Jalali/Gregorian conversion uses the Borkowski algorithm
// (K. M. Borkowski, "The Persian calendar for 3000 years", 1996).
// The algorithm is exact for Jalaali years -61..3177 (Gregorian 560..3798).
// The implementation is an inline, dep-free port of the well-known
// jalaali-js library — ~50 lines of low-level integer math; no allocations
// on the hot path.

const BREAKS = [
  -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181,
  1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178,
] as const;

const MIN_JY = BREAKS[0];
const MAX_JY = BREAKS[BREAKS.length - 1]! - 1;

function div(a: number, b: number): number {
  return ~~(a / b);
}
function mod(a: number, b: number): number {
  return a - ~~(a / b) * b;
}

function jalCal(jy: number): { leap: number; gy: number; march: number } {
  if (!Number.isFinite(jy) || jy < MIN_JY || jy > MAX_JY) {
    throw new RangeError(`Invalid Jalaali year ${jy}`);
  }
  const gy = jy + 621;
  let leapJ = -14;
  let jp: number = BREAKS[0]!;
  let jm = 0;
  let jump = 0;
  for (let i = 1; i < BREAKS.length; i += 1) {
    jm = BREAKS[i]!;
    jump = jm - jp;
    if (jy < jm) break;
    leapJ = leapJ + div(jump, 33) * 8 + div(mod(jump, 33), 4);
    jp = jm;
  }
  const n = jy - jp;
  leapJ = leapJ + div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1;
  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
  const march = 20 + leapJ - leapG;
  // leap = years since the last leap year (0 means this year is leap)
  let adjusted = n;
  if (jump - n < 6) {
    adjusted = n - jump + div(jump + 4, 33) * 33;
  }
  let leap = mod(mod(adjusted + 1, 33) - 1, 4);
  if (leap === -1) leap = 4;
  return { leap, gy, march };
}

function g2d(gy: number, gm: number, gd: number): number {
  let d =
    div((gy + div(gm - 8, 6) + 100100) * 1461, 4) +
    div(153 * mod(gm + 9, 12) + 2, 5) +
    gd -
    34840408;
  d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
  return d;
}

function d2g(jdn: number): { jy: number; jm: number; jd: number } {
  let j = 4 * jdn + 139361631;
  j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = div(mod(j, 1461), 4) * 5 + 308;
  const gd = div(mod(i, 153), 5) + 1;
  const gm = mod(div(i, 153), 12) + 1;
  const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
  return { jy: gy, jm: gm, jd: gd };
}

function j2d(jy: number, jm: number, jd: number): number {
  const r = jalCal(jy);
  return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
}

function d2j(jdn: number): { jy: number; jm: number; jd: number } {
  // Walk to the Gregorian year, then convert to its Jalaali counterpart.
  const gy = d2g(jdn).jy;
  let jy = gy - 621;
  const r = jalCal(jy);
  const jdn1f = g2d(gy, 3, r.march);
  // Days since Farvardin 1.
  let k = jdn - jdn1f;
  if (k >= 0) {
    if (k <= 185) {
      // First six months are 31 days each.
      return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 };
    }
    // The remaining months are 30 days each.
    k -= 186;
  } else {
    // The JDN falls in the *previous* Jalaali year.
    jy -= 1;
    k += 179;
    if (r.leap === 1) k += 1;
  }
  return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 };
}

function toJalaali(gy: number, gm: number, gd: number): { jy: number; jm: number; jd: number } {
  return d2j(g2d(gy, gm, gd));
}

function fromJalaali(jy: number, jm: number, jd: number): { jy: number; jm: number; jd: number } {
  return d2g(j2d(jy, jm, jd));
}

// --- Public API ---------------------------------------------------------

export function today(timezone: string = 'UTC'): string {
  // Returns ISO YYYY-MM-DD for the given IANA timezone, using the Intl API.
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date()); // 'en-CA' yields YYYY-MM-DD
}

export function gregorianToJalali(isoDate: string): { jy: number; jm: number; jd: number } {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return toJalaali(y, m, d);
}

export function jalaliToGregorian(jy: number, jm: number, jd: number): string {
  const g = fromJalaali(jy, jm, jd);
  const mm = String(g.jm).padStart(2, '0');
  const dd = String(g.jd).padStart(2, '0');
  return `${g.jy}-${mm}-${dd}`;
}

export function formatJalaliShort(isoDate: string): string {
  const { jy, jm, jd } = gregorianToJalali(isoDate);
  return `${jy}/${String(jm).padStart(2, '0')}/${String(jd).padStart(2, '0')}`;
}

const JALALI_MONTHS = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
] as const;

const JALALI_WEEKDAYS = [
  'شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه',
] as const;

export function formatJalaliLong(isoDate: string): string {
  const { jy, jm, jd } = gregorianToJalali(isoDate);
  const monthName = JALALI_MONTHS[jm - 1] ?? '';
  return `${jd} ${monthName} ${jy}`;
}

export function iranianDayOfWeek(isoDate: string): number {
  // Returns 0=Saturday, 1=Sunday, ..., 6=Friday
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const js = new Date(Date.UTC(y, m - 1, d));
  // JS: 0=Sun, 1=Mon, ..., 6=Sat. We want 0=Sat, 1=Sun, ..., 6=Fri.
  const jsDow = js.getUTCDay();
  return (jsDow + 1) % 7;
}

export function weekdayNamePersian(isoDate: string): string {
  return JALALI_WEEKDAYS[iranianDayOfWeek(isoDate)] ?? '';
}

export function dayOfMonth(isoDate: string): number {
  return Number(isoDate.split('-')[2]);
}
